import React, { useState, useCallback, useEffect } from "react";
import api from "../services/api";
import "./MegaAdminPanel.css";

interface LogEntry {
  _id: string;
  userId: {
    _id: string;
    name: string;
    phone: string;
    role: string;
  };
  userName: string;
  userRole: string;
  action: string;
  details: any;
  ip: string;
  userAgent: string;
  statut: string;
  createdAt: string;
}

interface DemandeEntry {
  _id: string;
  mariageId: {
    _id: string;
    bride: string;
    groom: string;
    date: string;
  };
  demandePar: {
    _id: string;
    name: string;
    role: string;
  };
  demandeParNom: string;
  demandeParRole: string;
  type: string;
  raison: string;
  statut: string;
  createdAt: string;
}

interface Stats {
  utilisateurs: {
    total: number;
    megaadmin: number;
    super_admin: number;
    admin: number;
    receptionniste: number;
    protocole: number;
  };
  mariages: {
    total: number;
    invites: number;
  };
  demandes: {
    enAttente: number;
    acceptees: number;
    refusees: number;
  };
  connexions: {
    aujourdhui: number;
  };
}

interface StatsResponse {
  success: boolean;
  stats: Stats;
}

interface LogsResponse {
  success: boolean;
  logs: LogEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface DemandesResponse {
  success: boolean;
  demandes: DemandeEntry[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface ActionResponse {
  success: boolean;
  message: string;
}

const MegaAdminPanel: React.FC = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [demandes, setDemandes] = useState<DemandeEntry[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [logsPage, setLogsPage] = useState(1);
  const [demandesPage, setDemandesPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [demandesTotal, setDemandesTotal] = useState(0);
  const [filterAction, setFilterAction] = useState("CREATION_UTILISATEUR");
  const [filterStatut, setFilterStatut] = useState("en_attente");
  const [showCommentModal, setShowCommentModal] = useState<{demande: DemandeEntry; action: 'accepter' | 'refuser'} | null>(null);
  const [commentaire, setCommentaire] = useState("");

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get<StatsResponse>("/megaadmin/statistiques");
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error("Erreur chargement stats:", error);
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    try {
      const response = await api.get<LogsResponse>(`/megaadmin/logs/actions?page=${logsPage}&limit=20&action=${filterAction}`);
      if (response.data.success) {
        setLogs(response.data.logs);
        setLogsTotal(response.data.pagination.total);
      }
    } catch (error) {
      console.error("Erreur chargement logs:", error);
    }
  }, [logsPage, filterAction]);

  const fetchDemandes = useCallback(async () => {
    try {
      const response = await api.get<DemandesResponse>(`/megaadmin/demandes?statut=${filterStatut}&page=${demandesPage}&limit=20`);
      if (response.data.success) {
        setDemandes(response.data.demandes);
        setDemandesTotal(response.data.pagination.total);
      }
    } catch (error) {
      console.error("Erreur chargement demandes:", error);
    }
  }, [demandesPage, filterStatut]);

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        fetchStats(),
        fetchLogs(),
        fetchDemandes()
      ]);
      setLoading(false);
    };
    loadAll();
  }, [fetchStats, fetchLogs, fetchDemandes, logsPage, demandesPage, filterAction, filterStatut]);

  const handleDeleteLog = async (logId: string) => {
    if (!window.confirm("Supprimer ce log ?")) return;
    try {
      await api.delete(`/megaadmin/logs/${logId}`);
      fetchLogs();
    } catch (error) {
      console.error("Erreur suppression log:", error);
    }
  };

  const handleDeleteAllLogs = async () => {
    if (!window.confirm("Supprimer TOUS les logs ? Cette action est irréversible.")) return;
    try {
      await api.delete("/megaadmin/logs/tous");
      fetchLogs();
      fetchStats();
    } catch (error) {
      console.error("Erreur suppression tous les logs:", error);
    }
  };

  const handleDeleteDemande = async (demandeId: string) => {
    if (!window.confirm("Supprimer cette demande ?")) return;
    try {
      await api.delete(`/megaadmin/demandes/${demandeId}`);
      fetchDemandes();
      fetchStats();
    } catch (error) {
      console.error("Erreur suppression demande:", error);
    }
  };

  const handleDeleteAllDemandes = async () => {
    if (!window.confirm("Supprimer TOUTES les demandes ? Cette action est irréversible.")) return;
    try {
      await api.delete("/megaadmin/demandes/tous");
      fetchDemandes();
      fetchStats();
    } catch (error) {
      console.error("Erreur suppression toutes les demandes:", error);
    }
  };

  const handleTraiterDemande = async (demandeId: string, action: 'accepter' | 'refuser') => {
    try {
      const response = await api.post<ActionResponse>(`/megaadmin/demandes/${demandeId}/${action}`, {
        commentaire
      });
      
      if (response.data.success) {
        alert(`Demande ${action === 'accepter' ? 'acceptée' : 'refusée'} avec succès`);
        setShowCommentModal(null);
        setCommentaire("");
        fetchDemandes();
        fetchStats();
      }
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors du traitement de la demande");
    }
  };

  const getActionColor = (action: string) => {
    const colors: {[key: string]: string} = {
      CREATION_UTILISATEUR: '#17a2b8',
      SUPPRESSION_UTILISATEUR: '#dc3545'
    };
    return colors[action] || '#6c757d';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActionText = (action: string) => {
    const texts: {[key: string]: string} = {
      CREATION_UTILISATEUR: 'Création',
      SUPPRESSION_UTILISATEUR: 'Suppression'
    };
    return texts[action] || action;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Chargement du panneau de supervision...</p>
      </div>
    );
  }

  return (
    <div className="mega-panel">
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Utilisateurs</h3>
            <div className="stat-number">{stats.utilisateurs.total}</div>
            <div className="stat-details">
              <span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
                </svg>
                Mega: {stats.utilisateurs.megaadmin}
              </span>
              <span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 12c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" fill="currentColor"/>
                </svg>
                Super: {stats.utilisateurs.super_admin}
              </span>
              <span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
                </svg>
                Admin: {stats.utilisateurs.admin}
              </span>
              <span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-1 .05 1.16.84 2 1.87 2 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
                </svg>
                Récep: {stats.utilisateurs.receptionniste}
              </span>
              <span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" fill="currentColor"/>
                </svg>
                Proto: {stats.utilisateurs.protocole}
              </span>
            </div>
          </div>

          <div className="stat-card">
            <h3>Mariages</h3>
            <div className="stat-number">{stats.mariages.total}</div>
            <div className="stat-details">
              <span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1s-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm0 4c2.76 0 5 2.24 5 5s-2.24 5-5 5-5-2.24-5-5 2.24-5 5-5z" fill="currentColor"/>
                </svg>
                Invités: {stats.mariages.invites}
              </span>
            </div>
          </div>

          <div className="stat-card">
            <h3>Demandes</h3>
            <div className="stat-number">{stats.demandes.enAttente}</div>
            <div className="stat-details">
              <span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
                </svg>
                Acceptées: {stats.demandes.acceptees}
              </span>
              <span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor"/>
                </svg>
                Refusées: {stats.demandes.refusees}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="panel-sections">
        <div className="section">
          <div className="section-header">
            <h3>Demandes en attente</h3>
            <button 
              type="button"
              className="btn-delete-all" 
              onClick={handleDeleteAllDemandes}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
              </svg>
              Tout supprimer
            </button>
          </div>
          <div className="filter-tabs">
            <button 
              type="button"
              className={filterStatut === 'en_attente' ? 'active' : ''}
              onClick={() => setFilterStatut('en_attente')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.4 0-8-3.6-8-8s3.6-8 8-8 8 3.6 8 8-3.6 8-8 8zm.5-13H11v6l5.2 3.1.8-1.2-4.5-2.7V7z" fill="currentColor"/>
              </svg>
              En attente
            </button>
            <button 
              type="button"
              className={filterStatut === 'acceptee' ? 'active' : ''}
              onClick={() => setFilterStatut('acceptee')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
              </svg>
              Acceptées
            </button>
            <button 
              type="button"
              className={filterStatut === 'refusee' ? 'active' : ''}
              onClick={() => setFilterStatut('refusee')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor"/>
              </svg>
              Refusées
            </button>
          </div>

          <div className="demandes-list">
            {demandes.length === 0 ? (
              <p className="empty-message">Aucune demande trouvée</p>
            ) : (
              demandes.map(demande => (
                <div key={demande._id} className="demande-card">
                  <div className="demande-header">
                    <span className="demande-type">
                      {demande.type === 'suppression_mariage' ? 'Suppression mariage' : 'Suppression invités'}
                    </span>
                    <span className={`demande-statut ${demande.statut}`}>
                      {demande.statut === 'en_attente' ? 'En attente' : 
                       demande.statut === 'acceptee' ? 'Acceptée' : 'Refusée'}
                    </span>
                    <button 
                      type="button"
                      className="btn-delete-small"
                      onClick={() => handleDeleteDemande(demande._id)}
                      title="Supprimer"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                      </svg>
                    </button>
                  </div>
                  
                  <div className="demande-mariage">
                    {demande.mariageId?.bride} & {demande.mariageId?.groom}
                  </div>
                  
                  <div className="demande-info">
                    <strong>Par:</strong> {demande.demandeParNom} ({demande.demandeParRole})
                  </div>
                  
                  <div className="demande-info">
                    <strong>Date:</strong> {formatDate(demande.createdAt)}
                  </div>
                  
                  <div className="demande-raison">
                    "{demande.raison}"
                  </div>

                  {demande.statut === 'en_attente' && (
                    <div className="demande-actions">
                      <button 
                        type="button"
                        className="btn-accepter"
                        onClick={() => setShowCommentModal({ demande, action: 'accepter' })}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
                        </svg>
                        Accepter
                      </button>
                      <button 
                        type="button"
                        className="btn-refuser"
                        onClick={() => setShowCommentModal({ demande, action: 'refuser' })}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor"/>
                        </svg>
                        Refuser
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {demandesTotal > 20 && (
            <div className="pagination">
              <button 
                type="button"
                onClick={() => setDemandesPage(p => Math.max(1, p-1))}
                disabled={demandesPage === 1}
              >
                Précédent
              </button>
              <span>Page {demandesPage} / {Math.ceil(demandesTotal / 20)}</span>
              <button 
                type="button"
                onClick={() => setDemandesPage(p => p+1)}
                disabled={demandesPage >= Math.ceil(demandesTotal / 20)}
              >
                Suivant
              </button>
            </div>
          )}
        </div>

        <div className="section">
          <div className="section-header">
            <h3>Créations / Suppressions</h3>
            <button 
              type="button"
              className="btn-delete-all" 
              onClick={handleDeleteAllLogs}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
              </svg>
              Tout supprimer
            </button>
          </div>
          <div className="filter-tabs">
            <button 
              type="button"
              className={filterAction === 'CREATION_UTILISATEUR' ? 'active' : ''}
              onClick={() => setFilterAction('CREATION_UTILISATEUR')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
              </svg>
              Créations
            </button>
            <button 
              type="button"
              className={filterAction === 'SUPPRESSION_UTILISATEUR' ? 'active' : ''}
              onClick={() => setFilterAction('SUPPRESSION_UTILISATEUR')}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
              </svg>
              Suppressions
            </button>
          </div>

          <div className="logs-list">
            {logs.map(log => (
              <div key={log._id} className="log-entry" style={{ borderLeftColor: getActionColor(log.action) }}>
                <div className="log-header">
                  <span className="log-action" style={{ backgroundColor: getActionColor(log.action) }}>
                    {getActionText(log.action)}
                  </span>
                  <span className="log-time">{formatDate(log.createdAt)}</span>
                  <button 
                    type="button"
                    className="btn-delete-small"
                    onClick={() => handleDeleteLog(log._id)}
                    title="Supprimer"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                    </svg>
                  </button>
                </div>
                
                <div className="log-user">
                  {log.userName} ({log.userRole})
                </div>

                <div className="log-details">
                  {log.action === 'CREATION_UTILISATEUR' && log.details?.body && (
                    <div>Création de {log.details.body.name} ({log.details.body.role})</div>
                  )}
                  {log.action === 'SUPPRESSION_UTILISATEUR' && log.details?.deletedUserName && (
                    <div>Suppression de {log.details.deletedUserName}</div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {logsTotal > 20 && (
            <div className="pagination">
              <button 
                type="button"
                onClick={() => setLogsPage(p => Math.max(1, p-1))}
                disabled={logsPage === 1}
              >
                Précédent
              </button>
              <span>Page {logsPage} / {Math.ceil(logsTotal / 20)}</span>
              <button 
                type="button"
                onClick={() => setLogsPage(p => p+1)}
                disabled={logsPage >= Math.ceil(logsTotal / 20)}
              >
                Suivant
              </button>
            </div>
          )}
        </div>
      </div>

      {showCommentModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>
              {showCommentModal.action === 'accepter' ? 'Accepter la demande' : 'Refuser la demande'}
            </h3>
            
            <div className="form-group">
              <label>Commentaire (optionnel)</label>
              <textarea
                value={commentaire}
                onChange={(e) => setCommentaire(e.target.value)}
                placeholder="Ajoutez un commentaire pour le demandeur..."
                rows={4}
              />
            </div>

            <div className="modal-actions">
              <button 
                type="button"
                className="btn-annuler"
                onClick={() => {
                  setShowCommentModal(null);
                  setCommentaire("");
                }}
              >
                Annuler
              </button>
              <button 
                type="button"
                className={showCommentModal.action === 'accepter' ? 'btn-accepter' : 'btn-refuser'}
                onClick={() => handleTraiterDemande(showCommentModal.demande._id, showCommentModal.action)}
              >
                {showCommentModal.action === 'accepter' ? 'Accepter' : 'Refuser'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MegaAdminPanel;